import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'ff_recent_searches';
const MAX_RECENT = 15;

function loadRecent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveRecent(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {}
}

export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState(() => loadRecent());

  useEffect(() => {
    function handleStorage(event) {
      if (event.key && event.key !== STORAGE_KEY) return;
      setRecentSearches(loadRecent());
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const addRecentSearch = useCallback((prediction) => {
    if (!prediction?.description) return;
    setRecentSearches((prev) => {
      const filtered = prev.filter(
        (r) => r.description.toLowerCase() !== prediction.description.toLowerCase()
      );
      const entry = {
        description: prediction.description,
        place_id: prediction.place_id || `recent-${Date.now()}`,
        structured_formatting: prediction.structured_formatting || {
          main_text: prediction.description,
          secondary_text: '',
        },
      };
      const next = [entry, ...filtered].slice(0, MAX_RECENT);
      saveRecent(next);
      return next;
    });
  }, []);

  const removeRecentSearch = useCallback((predictionOrDescription) => {
    const description = typeof predictionOrDescription === 'string'
      ? predictionOrDescription
      : predictionOrDescription?.description;
    if (!description) return;
    setRecentSearches((prev) => {
      const next = prev.filter(
        (r) => r.description.toLowerCase() !== String(description).toLowerCase()
      );
      saveRecent(next);
      return next;
    });
  }, []);

  const getFilteredRecent = useCallback((input) => {
    const recents = recentSearches.map((r) => ({ ...r, isRecent: true }));
    if (!input || input.trim().length < 1) return recents;
    const trimmed = input.trim().toLowerCase();
    return recents.filter((r) => r.description.toLowerCase().includes(trimmed));
  }, [recentSearches]);

  return { recentSearches, addRecentSearch, removeRecentSearch, getFilteredRecent };
}
